// My Profile → Portfolio (BA P1.6a): the BA edits her private draft, submits it
// with consent, withdraws a submission or removes herself from the catalogue,
// and previews it as a client sees it. The live version stays visible to
// clients while an edit is with the company for review (decision E).
// Presentation only — employee-service enforces every rule.
//  - AC1: a save sends only the fields she changed (never images/portrait).
//  - AC2: each field's label comes from the API's draft view after a save.
//  - AC3: the preview is the API's client view, never rendered from the form.
//  - AC4: removal copy states the 15-minute image-link bound.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Plus, Send, Trash2, Undo2 } from 'lucide-react';
import { useAppSelector } from '@/app/hooks';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Textarea } from '@/features/shared/components/ui/textarea';
import { Checkbox } from '@/features/shared/components/ui/checkbox';
import { DataState } from '@/features/shared/components/ui/DataState';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/features/shared/components/ui/dialog';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  BaContent, BaExperience, MyBaProfile, getMyBaPreview, getMyBaProfile, saveMyBaDraft, submitMyBaProfile,
  unpublishMyBaProfile, withdrawMyBaSubmission,
} from '@/services/baProfileService';
import { VerificationLabel } from '../components/ClientProfileView';
import { ClientPreview } from '../components/ClientPreview';
import { StatusBanner } from '../components/StatusBanner';
import {
  BA_CONSENT_TEXT, BA_CONSENT_VERSION, BA_REVOCATION_TEXT, ContentField, FIELD_LABELS,
  draftChanges, missingForSubmit, splitList, verificationOf,
} from '../portfolio';

type Form = { display_name: string; bio: string; skills: string; languages: string; service_areas: string;
  experience: BaExperience[] };

const toForm = (d: BaContent): Form => ({
  display_name: d.display_name ?? '', bio: d.bio ?? '',
  skills: (d.skills ?? []).join(', '), languages: (d.languages ?? []).join(', '),
  service_areas: (d.service_areas ?? []).join(', '), experience: d.experience ?? [],
});

const toContent = (f: Form): BaContent => ({
  display_name: f.display_name, bio: f.bio, skills: splitList(f.skills), languages: splitList(f.languages),
  service_areas: splitList(f.service_areas),
  experience: f.experience.filter((e) => e.title.trim()).map((e) => ({
    title: e.title.trim(), ...(e.detail?.trim() ? { detail: e.detail.trim() } : {}), ...(e.year ? { year: e.year } : {}),
  })),
});

export const MyPortfolioPage: React.FC = () => {
  const { t } = useTranslation('brandAmbassadors');
  const confirm = useConfirm();
  const company = useAppSelector((s) => s.tenant?.current?.name) || t('company_fallback', { defaultValue: 'your company' });
  const [state, setState] = useState<'loading' | 'ready' | 'unavailable' | 'error'>('loading');
  const [error, setError] = useState('');
  const [p, setP] = useState<MyBaProfile | null>(null);
  const [form, setForm] = useState<Form>(toForm({}));
  const [busy, setBusy] = useState(false);
  const [consentOpen, setConsentOpen] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [preview, setPreview] = useState<'draft' | 'published'>('draft'); // the BA's own preview: never a 'revision'

  const take = (next: MyBaProfile) => { setP(next); setForm(toForm(next.draft || {})); };

  const load = useCallback(() => {
    setState('loading');
    getMyBaProfile()
      .then((r) => { take(r); setState('ready'); })
      .catch((e) => {
        // 404 = BA isn't on for this company (flag or entitlement): "not available", never an error.
        if (e?.response?.status === 404) { setState('unavailable'); return; }
        setError(getApiErrorMessage(e, t('errors.load', { defaultValue: 'Could not load your portfolio.' }) as string));
        setState('error');
      });
  }, [t]);
  useEffect(load, [load]);

  const changes = useMemo(() => draftChanges(toContent(form), p?.draft), [p, form]);
  const saved = Object.keys(changes).length === 0;
  // AC2: the label is the API's draft view (after a save), never guessed from the form.
  const label = (f: ContentField, item?: number) => (
    <VerificationLabel value={verificationOf(f, p?.draft_verification, item)} company={company} self />
  );
  const locked = p?.draft_status === 'submitted';
  const missing = missingForSubmit(toContent(form));

  const act = async (fn: () => Promise<MyBaProfile>, ok: string) => {
    setBusy(true);
    try { take(await fn()); toast.success(ok); return true; } catch (e) {
      toast.error(getApiErrorMessage(e, t('errors.action', { defaultValue: 'That didn’t work. Try again.' }) as string));
      return false;
    } finally { setBusy(false); }
  };

  if (state === 'loading') return <DataState variant="loading" />;
  if (state === 'unavailable') {
    return <DataState variant="unavailable" message={t('unavailable', { defaultValue: 'Brand Ambassador portfolios aren’t available for your company.' }) as string} />;
  }
  if (state === 'error' || !p) return <DataState variant="error" message={error} onRetry={load} />;

  const set = (k: keyof Form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));
  const setExp = (i: number, patch: Partial<BaExperience>) =>
    setForm((f) => ({ ...f, experience: f.experience.map((x, j) => (j === i ? { ...x, ...patch } : x)) }));

  const live = p.publication.status === 'published' && !!p.published_revision;

  return (
    <div className="space-y-4 p-4 md:p-6 max-w-5xl">
      <div>
        <h1 className="text-h2 text-foreground">{t('my.title', { defaultValue: 'My portfolio' })}</h1>
        <p className="text-sm text-muted-foreground">
          {t('my.subtitle', { defaultValue: 'What {{company}}’s clients see when they look for a Brand Ambassador.', company })}
        </p>
      </div>

      <StatusBanner profile={p} />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle>{t('my.edit', { defaultValue: 'Your profile' })}</CardTitle></CardHeader>
          <CardContent className="space-y-3" data-testid="editor">
            {locked && (
              <p className="text-sm text-muted-foreground" data-testid="locked-note">
                {t('my.locked', { defaultValue: 'Your profile is with {{company}} for review. Withdraw the submission to edit it.', company })}
              </p>
            )}
            <label className="block space-y-1 text-sm">
              <span className="flex items-center justify-between font-medium">{t('fields.display_name_for_clients', { defaultValue: 'Display name for clients' })}{label('display_name')}</span>
              <Input value={form.display_name} onChange={set('display_name')} maxLength={80} disabled={locked} />
              <span className="block text-xs text-muted-foreground" data-testid="display-name-hint">
                {t('fields.display_name_hint', { defaultValue: 'First name and initial, e.g. “Amina K.”. Your full name and contacts stay private.' })}
              </span>
            </label>
            <label className="block space-y-1 text-sm">
              <span className="flex items-center justify-between font-medium">{t('fields.bio', { defaultValue: 'Introduction' })}{label('bio')}</span>
              <Textarea value={form.bio} onChange={set('bio')} maxLength={2000} rows={5} disabled={locked} />
            </label>
            {(['skills', 'languages', 'service_areas'] as const).map((k) => (
              <label key={k} className="block space-y-1 text-sm">
                <span className="flex items-center justify-between font-medium" data-field={k}>{t(`fields.${k}`, { defaultValue: FIELD_LABELS[k] })}{label(k)}</span>
                <Input value={form[k]} onChange={set(k)} disabled={locked}
                  placeholder={t('fields.comma_hint', { defaultValue: 'Separate with commas' }) as string} />
              </label>
            ))}
            <div className="space-y-2">
              <span className="text-sm font-medium">{t('fields.experience', { defaultValue: 'Experience' })}</span>
              {form.experience.map((e, i) => (
                <div key={i} className="grid grid-cols-[1fr_6rem_auto_auto] items-center gap-2" data-item={i}>
                  <Input value={e.title} onChange={(ev) => setExp(i, { title: ev.target.value })} maxLength={120} disabled={locked}
                    aria-label={t('fields.exp_title', { defaultValue: 'Event or brand' }) as string}
                    placeholder={t('fields.exp_title', { defaultValue: 'Event or brand' }) as string} />
                  <Input type="number" value={e.year ?? ''} disabled={locked}
                    aria-label={t('fields.exp_year', { defaultValue: 'Year' }) as string}
                    onChange={(ev) => setExp(i, { year: ev.target.value ? Number(ev.target.value) : undefined })} />
                  <Button variant="ghost" size="sm" disabled={locked}
                    aria-label={t('actions.remove_item', { defaultValue: 'Remove' }) as string}
                    onClick={() => setForm((f) => ({ ...f, experience: f.experience.filter((_, j) => j !== i) }))}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  {label('experience', i)}
                </div>
              ))}
              <Button variant="outline" size="sm" disabled={locked || form.experience.length >= 20}
                onClick={() => setForm((f) => ({ ...f, experience: [...f.experience, { title: '' }] }))}>
                <Plus className="w-4 h-4 mr-1" />{t('actions.add_experience', { defaultValue: 'Add experience' })}
              </Button>
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              {!locked && (
                <Button variant="outline" disabled={busy || saved}
                  onClick={() => act(() => saveMyBaDraft(changes, p.version), t('toast.saved', { defaultValue: 'Draft saved.' }) as string)}>
                  {t('actions.save', { defaultValue: 'Save draft' })}
                </Button>
              )}
              {!locked && (
                <Button disabled={busy || !saved || missing.length > 0} onClick={() => { setAgreed(false); setConsentOpen(true); }}>
                  <Send className="w-4 h-4 mr-1" />{t('actions.submit', { defaultValue: 'Submit for review' })}
                </Button>
              )}
              {locked && (
                <Button variant="outline" disabled={busy}
                  onClick={() => act(withdrawMyBaSubmission, t('toast.withdrawn', { defaultValue: 'Submission withdrawn. You can edit again.' }) as string)}>
                  <Undo2 className="w-4 h-4 mr-1" />{t('actions.withdraw', { defaultValue: 'Withdraw submission' })}
                </Button>
              )}
              {live && (
                <Button variant="outline" className="text-danger" disabled={busy}
                  onClick={async () => {
                    const yes = await confirm({
                      title: t('remove.title', { defaultValue: 'Remove me from the catalogue' }) as string,
                      message: t('remove.message', { defaultValue: `${BA_REVOCATION_TEXT} Your assignment history is kept.` }) as string,
                      confirmLabel: t('remove.confirm', { defaultValue: 'Remove me' }) as string,
                      danger: true,
                    });
                    if (yes) await act(unpublishMyBaProfile, t('toast.removed', { defaultValue: 'You’re no longer in the catalogue.' }) as string);
                  }}>
                  {t('actions.remove', { defaultValue: 'Remove me from the catalogue' })}
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground" data-testid="offboarding-help">
              {t('my.offboarding_help', { defaultValue: `If you stop working with {{company}}, your profile is removed from the catalogue automatically. ${BA_REVOCATION_TEXT}`, company })}
            </p>
            {!locked && !saved && (
              <p className="text-xs text-muted-foreground">{t('my.save_first', { defaultValue: 'Save your changes before submitting.' })}</p>
            )}
            {!locked && saved && missing.length > 0 && (
              <p className="text-xs text-muted-foreground" data-testid="missing">
                {t('my.missing', { defaultValue: 'To submit, fill in: {{fields}}.',
                  fields: missing.map((m) => t(`fields.${m}`, { defaultValue: FIELD_LABELS[m] })).join(', ') })}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2">
            <CardTitle>{t('preview.title', { defaultValue: 'Preview as client' })}</CardTitle>
            <div className="flex gap-1" role="group" aria-label={t('preview.title', { defaultValue: 'Preview as client' }) as string}>
              <Button size="sm" variant={preview === 'draft' ? 'default' : 'outline'} aria-pressed={preview === 'draft'}
                onClick={() => setPreview('draft')}>{t('preview.draft', { defaultValue: 'Draft' })}</Button>
              <Button size="sm" variant={preview === 'published' ? 'default' : 'outline'} aria-pressed={preview === 'published'}
                disabled={!p.published_revision} onClick={() => setPreview('published')}>
                {t('preview.published', { defaultValue: 'Published' })}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <ClientPreview company={company} load={() => getMyBaPreview(preview)} reloadKey={`${preview}:${p.version}:${p.publication.version}`} />
          </CardContent>
        </Card>
      </div>

      <Dialog open={consentOpen} onOpenChange={setConsentOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('consent.title', { defaultValue: 'Agree to publication' })}</DialogTitle></DialogHeader>
          <p className="text-sm text-foreground" data-testid="consent-text">{t('consent.text', { defaultValue: BA_CONSENT_TEXT, company })}</p>
          <label className="flex items-start gap-2 text-sm">
            <Checkbox checked={agreed} onCheckedChange={(v) => setAgreed(v === true)} aria-label={t('consent.agree', { defaultValue: 'I agree' }) as string} />
            <span>{t('consent.agree', { defaultValue: 'I agree' })}</span>
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConsentOpen(false)}>{t('actions.cancel', { defaultValue: 'Cancel' })}</Button>
            <Button disabled={!agreed || busy} onClick={async () => {
              if (await act(() => submitMyBaProfile(BA_CONSENT_VERSION, p.version),
                t('toast.submitted', { defaultValue: 'Sent to {{company}} for review.', company }) as string)) setConsentOpen(false);
            }}>
              {t('actions.agree_submit', { defaultValue: 'Agree and submit' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyPortfolioPage;
